import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { getAdminOrders, type AdminOrderStatus } from "@/lib/admin-api-client";
import { describeAdminError } from "@/components/admin/adminErrors";
import { formatDateTimeFR } from "@/components/admin/adminFormat";

/** Cf. `OrderStatus` (packages/db/prisma/schema.prisma §7.1). */
const STATUS_OPTIONS: Array<{ value: AdminOrderStatus; label: string }> = [
  { value: "DRAFT", label: "Brouillon" },
  { value: "PENDING_CONFIRMATION", label: "En attente de confirmation" },
  { value: "CONFIRMED", label: "Confirmée" },
  { value: "AWAITING_PAYMENT", label: "En attente de paiement" },
  { value: "PAID", label: "Payée" },
  { value: "PROCESSING", label: "En préparation" },
  { value: "READY_TO_SHIP", label: "Prête à expédier" },
  { value: "SHIPPED", label: "Expédiée" },
  { value: "DELIVERY_FAILED", label: "Échec de livraison" },
  { value: "DELIVERED", label: "Livrée" },
  { value: "RETURN_REQUESTED", label: "Retour demandé" },
  { value: "RETURNED", label: "Retournée" },
  { value: "REFUNDED", label: "Remboursée" },
  { value: "COMPLETED", label: "Terminée" },
  { value: "CANCELLED", label: "Annulée" },
  { value: "RETURNED_TO_SENDER", label: "Retournée à l'expéditeur" },
];

function pageHref(status: string | undefined, search: string | undefined, page: number): string {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (search) params.set("search", search);
  if (page > 1) params.set("page", String(page));
  const qs = params.toString();
  return qs ? `/admin/commandes?${qs}` : "/admin/commandes";
}

const PAGE_SIZE = 20;

/**
 * `/admin/commandes` — équivalent de apps/web/app/(admin)/admin/commandes/page.tsx :
 * file de confirmation téléphonique et listing général des commandes
 * (VVS_MODELE_INTERFACE.md §15). Filtres pilotés par l'URL
 * (`?status=&search=&page=`) via `useSearchParams`, comme l'original avec un
 * formulaire GET natif.
 */
export function AdminCommandesPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const status = (searchParams.get("status") as AdminOrderStatus | null) ?? undefined;
  const search = searchParams.get("search")?.trim() || undefined;
  const pageParam = Number.parseInt(searchParams.get("page") ?? "1", 10);
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin", "orders", { status, search, page, pageSize: PAGE_SIZE }],
    queryFn: () => getAdminOrders(undefined, { status, search, page, pageSize: PAGE_SIZE }),
    placeholderData: (prev) => prev,
  });

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const next = new URLSearchParams();
    const nextStatus = String(formData.get("status") ?? "").trim();
    const nextSearch = String(formData.get("search") ?? "").trim();
    if (nextStatus) next.set("status", nextStatus);
    if (nextSearch) next.set("search", nextSearch);
    setSearchParams(next);
  }

  if (isError) {
    return (
      <div>
        <h1 className="font-display text-2xl font-semibold text-ink">Commandes</h1>
        <p className="mt-3 text-sm text-red-700">{describeAdminError(error)}</p>
      </div>
    );
  }

  const items = data?.items ?? [];
  const totalItems = data?.totalItems ?? 0;
  const totalPages = data?.totalPages ?? 1;

  return (
    <div>
      <h1 className="font-display text-2xl font-semibold text-ink">Commandes</h1>
      <p className="mt-1 text-sm text-muted">
        {isLoading && !data
          ? "Chargement…"
          : `${totalItems} commande${totalItems > 1 ? "s" : ""} — page ${page}/${totalPages}`}
      </p>

      <form onSubmit={handleFilterSubmit} className="mt-6 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor="admin-orders-status" className="text-xs font-medium uppercase tracking-wide text-navy">
            Statut
          </label>
          <select
            id="admin-orders-status"
            name="status"
            defaultValue={status ?? ""}
            className="min-w-[220px] rounded-sm border border-line bg-white px-3 py-2 text-sm text-ink focus:border-gold focus:outline-none"
          >
            <option value="">Tous les statuts</option>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="admin-orders-search" className="text-xs font-medium uppercase tracking-wide text-navy">
            Recherche
          </label>
          <input
            id="admin-orders-search"
            type="text"
            name="search"
            defaultValue={search ?? ""}
            placeholder="N° de commande, nom, téléphone…"
            className="min-w-[260px] rounded-sm border border-line bg-white px-3 py-2 text-sm text-ink focus:border-gold focus:outline-none"
          />
        </div>

        <button
          type="submit"
          className="rounded-sm border border-gold bg-gold px-4 py-2 text-xs font-medium uppercase tracking-wide text-navy hover:border-gold2 hover:bg-gold2"
        >
          Filtrer
        </button>

        {status || search ? (
          <Link to="/admin/commandes" className="text-xs font-medium text-navy underline">
            Réinitialiser
          </Link>
        ) : null}
      </form>

      <div className="mt-6 overflow-x-auto rounded-sm border border-line bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-line bg-cream2 text-xs font-medium uppercase tracking-wide text-muted">
            <tr>
              <th className="px-4 py-3">N° commande</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3">Paiement</th>
              <th className="px-4 py-3">Passée le</th>
              <th className="px-4 py-3">Total</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {isLoading && !data ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted">
                  Chargement…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted">
                  Aucune commande ne correspond à ces filtres.
                </td>
              </tr>
            ) : (
              items.map((order) => (
                <tr key={order.id} className="border-b border-line last:border-0 hover:bg-cream2">
                  <td className="px-4 py-3 font-medium text-ink">{order.orderNumber}</td>
                  <td className="px-4 py-3">{order.status}</td>
                  <td className="px-4 py-3">{order.paymentStatus}</td>
                  <td className="px-4 py-3">{formatDateTimeFR(order.placedAt)}</td>
                  <td className="px-4 py-3">{order.grandTotal.formatted ?? `${order.grandTotal.amountMinor}`}</td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/admin/commandes/${order.id}`} className="font-medium text-navy hover:underline">
                      Voir →
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between">
        {page > 1 ? (
          <Link to={pageHref(status, search, page - 1)} className="text-sm font-medium text-navy hover:underline">
            ← Page précédente
          </Link>
        ) : (
          <span />
        )}
        {page < totalPages ? (
          <Link to={pageHref(status, search, page + 1)} className="text-sm font-medium text-navy hover:underline">
            Page suivante →
          </Link>
        ) : null}
      </div>
    </div>
  );
}
